import React, { useEffect } from "react";
import toast from "react-hot-toast";
import styles from "./SaveSessionToast.module.css";

const formatMSS = (seconds) => {
  const s = Math.max(0, Math.floor(seconds || 0));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
};

/**
 * Save-session confirmation toast for the monitoring panel.
 *
 * Renders the snapshot + manual merge math:
 *   total = snapshot (captured at click) + manual (user typed).
 *
 * The Manual row shows EXACTLY the value the user typed — not a delta
 * from the snapshot. Total is passed in pre-summed so the toast can't
 * drift if the live timer ticks between save click and server response.
 *
 * When the user never engaged the field (snapshotSeconds === null) the
 * save used the live timer untouched and there's no merge to break
 * down — we collapse to a single "From live timer" row.
 *
 * Props:
 * - t: react-hot-toast Toast object (auto-injected via toast.custom).
 * - snapshotSeconds: live-timer value captured at first click. null if
 *   the user never engaged.
 * - manualSeconds: the time the user actually typed (not a delta).
 *   Only meaningful when snapshotSeconds !== null.
 * - totalSeconds: snapshot + manual when engaged, liveSeconds otherwise.
 * - program: "RPM" or "CCM" — shown as a small badge.
 */
const SaveSessionToast = ({
  t,
  snapshotSeconds,
  manualSeconds,
  totalSeconds,
  program = "RPM",
}) => {
  const isCcm = program === "CCM";
  const engaged = snapshotSeconds !== null && snapshotSeconds !== undefined;

  useEffect(() => {
    const announcement = `Session saved. Total ${formatMSS(totalSeconds)} for ${program}.`;
    const node = document.createElement("div");
    node.setAttribute("role", "status");
    node.setAttribute("aria-live", "polite");
    node.style.position = "absolute";
    node.style.left = "-9999px";
    node.style.width = "1px";
    node.style.height = "1px";
    node.textContent = announcement;
    document.body.appendChild(node);
    return () => {
      if (node.parentNode) node.parentNode.removeChild(node);
    };
  }, [program, totalSeconds]);

  return (
    <div
      className={`${styles.toast} ${t.visible ? styles.visible : styles.hidden}`}
      role="alert"
    >
      <span
        className={styles.accentStripe}
        style={isCcm ? { background: "#7c3aed" } : undefined}
        aria-hidden="true"
      />

      <div className={styles.body}>
        <div className={styles.header}>
          <span
            className={styles.checkBadge}
            style={isCcm ? { background: "#7c3aed" } : undefined}
            aria-hidden="true"
          >
            <svg
              width="11"
              height="11"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="5 12 10 17 19 8" />
            </svg>
          </span>
          <span className={styles.title}>Session saved</span>
          <span
            className={`${styles.programBadge} ${isCcm ? styles.programCcm : styles.programRpm}`}
          >
            {program}
          </span>
        </div>

        <div className={styles.breakdown}>
          {engaged ? (
            <>
              <div className={styles.row}>
                <span className={styles.rowLabel}>Snapshot</span>
                <span className={styles.rowValue}>
                  <span className={styles.op} aria-hidden="true">&nbsp;</span>
                  {formatMSS(snapshotSeconds)}
                </span>
              </div>
              <div className={styles.row}>
                <span className={styles.rowLabel}>Manual</span>
                <span className={styles.rowValue}>
                  <span className={styles.op} aria-hidden="true">+</span>
                  {formatMSS(manualSeconds)}
                </span>
              </div>
              <div className={styles.divider} aria-hidden="true" />
              <div className={`${styles.row} ${styles.totalRow}`}>
                <span className={styles.totalLabel}>Total</span>
                <span
                  className={styles.totalValue}
                  style={isCcm ? { color: "#7c3aed" } : undefined}
                >
                  {formatMSS(totalSeconds)}
                </span>
              </div>
            </>
          ) : (
            <div className={styles.singleSummary}>
              <span className={styles.rowLabel}>From live timer</span>
              <span
                className={styles.totalValue}
                style={isCcm ? { color: "#7c3aed" } : undefined}
              >
                {formatMSS(totalSeconds)}
              </span>
            </div>
          )}
        </div>
      </div>

      <button
        type="button"
        className={styles.dismissBtn}
        onClick={() => toast.dismiss(t.id)}
        aria-label="Dismiss"
      >
        <svg
          width="11"
          height="11"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  );
};

export default SaveSessionToast;
